import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { formatDate } from '../lib/orgTimezone';
import { humanizeToken } from '../lib/inlineAttribution';
import { Badge } from '../atoms';
import { Modal } from '../organisms';
import { QueryState, PortalTable, money } from './primitives';
import { PayInvoiceButton, type PayableInvoice } from './PortalPay';

/**
 * PortalDetail (#37) — read-only detail views for the client portal. Clicking a
 * record (property / parcel / appeal / invoice) opens a focused modal that shows
 * the record's fields — NO edit controls. The one affordance that ACTS is Pay,
 * which lives on the invoice detail (invoices are payable; everything else is
 * view-only). Detail views are built from the list-row data already fetched, plus
 * (for a property) its parcels via the portal parcels endpoint.
 */

const dt = (s?: string | null) => formatDate(s);

/** A label/value row for the read-only detail cards. */
function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '8px 0', borderBottom: '1px solid var(--color-border-subtle, #f0f0ee)', fontSize: 14 }}>
      <span style={{ color: 'var(--color-muted, #666)' }}>{label}</span>
      <span style={{ fontWeight: 500, textAlign: 'right', minWidth: 0 }}>{value ?? '—'}</span>
    </div>
  );
}

export interface PortalProperty {
  id: string; name: string | null; city: string | null; state: string | null;
  re_pp: string | null; prop_type: string | null; status: string | null;
}
export interface PortalParcel {
  id: string; account_number: string | null; name: string | null; re_pp: string | null;
  prop_type: string | null; current_market_value: number | null; proposed_market_value: number | null;
  under_appeal: boolean | number | null; status: string | null; property_id: string | null;
  property_name: string | null; state: string | null;
  /** ATA part 3 §6 glass case: the firm has not yet shared a value record for this parcel. */
  values_shared?: boolean; values_withheld?: boolean;
}

/** A value, or the glass-case placeholder when the firm has not shared it yet. */
export const portalValue = (p: { values_withheld?: boolean }, v: number | null) => (p.values_withheld ? 'Not yet shared' : money(v));

const rePpLabel = (v: string | null | undefined) => (v === 'personal_property' ? 'Personal property' : v === 'real_estate' ? 'Real estate' : (v || '—'));

/** Property detail — fields + its parcels (click a parcel → parcel detail). */
export function PropertyDetailModal({ property, onClose, onOpenParcel }: {
  property: PortalProperty; onClose: () => void; onOpenParcel: (p: PortalParcel) => void;
}) {
  const q = useQuery({
    queryKey: ['cp', 'parcels', property.id],
    queryFn: ({ signal }) => api.get<PortalParcel[]>(`/api/client-portal/parcels?property_id=${property.id}`, signal),
  });
  const parcels = q.data ?? [];
  return (
    <Modal open onClose={onClose} title={property.name || 'Property'} size="lg">
      <Row label="Name" value={property.name} />
      <Row label="City" value={property.city} />
      <Row label="State" value={property.state} />
      <Row label="Type" value={rePpLabel(property.re_pp)} />
      <Row label="Property type" value={property.prop_type} />
      <Row label="Status" value={<Badge variant="neutral">{humanizeToken(property.status || '')}</Badge>} />

      <div style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--color-muted, #666)', margin: '20px 0 8px' }}>
        Parcels
      </div>
      <QueryState isLoading={q.isLoading} error={q.error} isEmpty={parcels.length === 0} emptyLabel="No parcels on this property.">
        <PortalTable
          rows={parcels}
          getKey={(r) => r.id}
          onRowClick={onOpenParcel}
          columns={[
            { header: 'Account #', cell: (r) => r.account_number || '—' },
            { header: 'Name', cell: (r) => r.name || '—' },
            { header: 'Current value', align: 'right', cell: (r) => portalValue(r, r.current_market_value) },
            { header: 'Appeal', cell: (r) => (r.under_appeal ? <Badge variant="warning">Under appeal</Badge> : '—') },
          ]}
        />
      </QueryState>
    </Modal>
  );
}

/** Parcel detail — read-only. */
export function ParcelDetailModal({ parcel, onClose }: { parcel: PortalParcel; onClose: () => void }) {
  return (
    <Modal open onClose={onClose} title={parcel.name || parcel.account_number || 'Parcel'} size="lg">
      <Row label="Account #" value={parcel.account_number} />
      <Row label="Name" value={parcel.name} />
      <Row label="Property" value={parcel.property_name} />
      <Row label="Type" value={rePpLabel(parcel.re_pp)} />
      <Row label="Current market value" value={portalValue(parcel, parcel.current_market_value)} />
      <Row label="Proposed market value" value={portalValue(parcel, parcel.proposed_market_value)} />
      <Row label="Under appeal" value={parcel.under_appeal ? 'Yes' : 'No'} />
      <Row label="Status" value={<Badge variant="neutral">{humanizeToken(parcel.status || '')}</Badge>} />
    </Modal>
  );
}

export interface PortalAppeal {
  id: string; lifecycle_stage: string | null; tax_year: number | null; hearing_date: string | null;
  board_order_expected_date: string | null; board_order_received_date: string | null;
  litigation_deadline_date: string | null; outcome: string | null;
  account_number: string | null; parcel_name: string | null;
}

/** Appeal detail — read-only. */
export function AppealDetailModal({ appeal, onClose }: { appeal: PortalAppeal; onClose: () => void }) {
  return (
    <Modal open onClose={onClose} title={`Appeal — ${appeal.parcel_name || appeal.account_number || ''}`.trim()} size="lg">
      <Row label="Parcel" value={appeal.parcel_name || appeal.account_number} />
      <Row label="Tax year" value={appeal.tax_year} />
      <Row label="Stage" value={<Badge variant="info">{String(appeal.lifecycle_stage || '').replace(/_/g, ' ') || '—'}</Badge>} />
      <Row label="Hearing" value={dt(appeal.hearing_date)} />
      <Row label="Board order expected" value={dt(appeal.board_order_expected_date)} />
      <Row label="Board order received" value={dt(appeal.board_order_received_date)} />
      <Row label="Litigation deadline" value={dt(appeal.litigation_deadline_date)} />
      <Row label="Outcome" value={appeal.outcome || '—'} />
    </Modal>
  );
}

export interface PortalInvoice {
  id: string; invoice_number: string | null; status: string; amount: number; balance: number;
  invoice_date: string | null; due_date: string | null;
}

export interface PortalInvoiceItem {
  id: string; description: string | null; amount: number | null; year: number | null;
  parcel_id: string | null; parcel_account: string | null; parcel_name: string | null;
}

/**
 * Invoice detail — read-only summary + the invoice's LINE ITEMS (account/parcel,
 * description, amount) fetched from the client-scoped portal endpoint. This mirrors
 * the staff InvoiceDetail line-items tab, but view-only and portal-scoped. The one
 * acting affordance is Pay (invoices are payable; everything else is view-only).
 */
export function InvoiceDetailModal({ invoice, onClose, onPaid }: {
  invoice: PortalInvoice; onClose: () => void; onPaid: () => void;
}) {
  const q = useQuery({
    queryKey: ['cp', 'invoice-items', invoice.id],
    queryFn: ({ signal }) => api.get<PortalInvoiceItem[]>(`/api/client-portal/invoices/${invoice.id}/items`, signal),
  });
  const items = q.data ?? [];
  const payable: PayableInvoice = {
    id: invoice.id, invoice_number: invoice.invoice_number,
    amount: Number(invoice.amount), balance: Number(invoice.balance), status: invoice.status,
  };
  return (
    <Modal open onClose={onClose} title={`Invoice #${invoice.invoice_number || invoice.id.slice(0, 8)}`} size="lg">
      <Row label="Invoice #" value={invoice.invoice_number || invoice.id.slice(0, 8)} />
      <Row label="Status" value={<Badge variant="info">{humanizeToken(invoice.status || '')}</Badge>} />
      <Row label="Amount" value={money(invoice.amount)} />
      <Row label="Balance" value={money(invoice.balance)} />
      <Row label="Invoice date" value={dt(invoice.invoice_date)} />
      <Row label="Due" value={dt(invoice.due_date)} />

      <div style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--color-muted, #666)', margin: '20px 0 8px' }}>
        Line items
      </div>
      <QueryState isLoading={q.isLoading} error={q.error} isEmpty={items.length === 0} emptyLabel="No line items on this invoice.">
        <PortalTable
          rows={items}
          getKey={(r) => r.id}
          columns={[
            { header: 'Account / parcel', cell: (r) => r.parcel_account || r.parcel_name || '—' },
            { header: 'Description', cell: (r) => r.description || '—' },
            { header: 'Year', cell: (r) => r.year ?? '—' },
            { header: 'Amount', align: 'right', cell: (r) => money(r.amount) },
          ]}
        />
      </QueryState>

      {/* Only offer Pay for a status the server will collect against — a balance
          alone isn't enough (a 'pending'/'prepared' invoice 400s the pay-intent).
          Mirrors PAYABLE_INVOICE_STATUSES minus 'paid' (which has no balance). */}
      {Number(invoice.balance) > 0 && ['approved', 'sent', 'partially_paid'].includes(invoice.status) && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <PayInvoiceButton invoice={payable} onPaid={() => { onPaid(); onClose(); }} />
        </div>
      )}
    </Modal>
  );
}
